import { createSelector, createSlice, PayloadAction, prepareAutoBatched } from '@reduxjs/toolkit';
import { LayoutOrderState, UserScreenshareSource } from 'features/layout/features/order/types';
import {
  ExternalStreamSourceDetails,
  ScreenshareSourceDetails,
  SourceDetails,
} from 'features/layout/types';
import { FeedId } from 'features/streaming/types';
import { sourceMatches } from 'features/streaming/utils';
import { selectLocalUser, selectUsers, selectUsersWithoutLocal } from 'features/users/usersSlice';
import { screenshareSelected } from 'features/screenshare/actions';
import { RootState } from 'store/store';

export const initialState: LayoutOrderState = {
  orderedStreams: [],
  screenshareStreams: [],
  externalStreams: [],
  historicalSpeakers: [],
};

const layoutOrderSlice = createSlice({
  name: 'layoutOrder',
  initialState,
  reducers: {
    orderUpdated: {
      reducer(state, action: PayloadAction<SourceDetails[]>) {
        state.orderedStreams = action.payload;
      },
      prepare: prepareAutoBatched<SourceDetails[]>(),
    },
    orderedStreamAdded(state, action: PayloadAction<SourceDetails>) {
      state.orderedStreams.push(action.payload);
    },
    orderedStreamDeleted(state, action: PayloadAction<SourceDetails>) {
      state.orderedStreams = state.orderedStreams.filter(
        (stream) => !sourceMatches(stream, action.payload)
      );
    },
    screenshareAdded(state, action: PayloadAction<ScreenshareSourceDetails>) {
      state.screenshareStreams = state.screenshareStreams.filter(
        (stream) => stream.userId !== action.payload.userId
      );

      state.screenshareStreams.push(action.payload);
    },
    screenshareDeleted(state, action: PayloadAction<SourceDetails>) {
      state.screenshareStreams = state.screenshareStreams.filter(
        (stream) => !sourceMatches(stream, action.payload)
      );
    },
    screensharesDeleted(state, action: PayloadAction<{ ids: FeedId[] }>) {
      const idSet = new Set(action.payload.ids);

      state.screenshareStreams = state.screenshareStreams.filter(
        (stream) => !stream.feedId || !idSet.has(stream.feedId)
      );
    },
    screenshareCollectionAdded(state, action: PayloadAction<ScreenshareSourceDetails[]>) {
      // only doing this once per room, shouldn’t be an issue;
      // state.screenshareStreams = [...new Set([...state.screenshareStreams, ...action.payload])];

      // replace to cleanup on reconnect; switch back to spread approach if this causes issues;
      state.screenshareStreams = action.payload;
    },
    externalStreamAdded(state, action: PayloadAction<ExternalStreamSourceDetails>) {
      const isExist = state.externalStreams.some(
        (stream) => stream.feedId === action.payload.feedId
      );

      if (!isExist) {
        state.externalStreams.push(action.payload);
      }
    },
    externalStreamDeleted(state, action: PayloadAction<SourceDetails>) {
      state.externalStreams = state.externalStreams.filter(
        (stream) => stream.feedId !== action.payload.feedId
      );
    },
    externalStreamsCleared(state) {
      state.externalStreams = [];
    },
    speakersUpdated: {
      reducer(state, action: PayloadAction<SourceDetails[]>) {
        state.historicalSpeakers = action.payload;
      },
      prepare: prepareAutoBatched<SourceDetails[]>(),
    },
  },
  extraReducers: (builder) => {
    builder.addCase(screenshareSelected, (state, action) => {
      if (action.payload.update) {
        const currentIndex = state.screenshareStreams.findIndex(
          (item) => item.feedId === action.payload.feedId
        );
        state.screenshareStreams[currentIndex] = {
          ...state.screenshareStreams[currentIndex],
          timestamp: new Date().getTime(),
        } as any;
      }
    });
  },
});

export const {
  orderUpdated,
  orderedStreamAdded,
  orderedStreamDeleted,
  speakersUpdated,
  screenshareDeleted,
  screensharesDeleted,
  screenshareAdded,
  screenshareCollectionAdded,
  externalStreamAdded,
  externalStreamDeleted,
  externalStreamsCleared,
} = layoutOrderSlice.actions;

export const selectOrderedStreams = (state: RootState) => state.layout.order.orderedStreams;
export const selectScreenshareStreams = createSelector(
  [selectUsers, (state: RootState) => state.layout.order.screenshareStreams],
  (users, screenshares) => {
    const formattedScreenshares = screenshares
      .map((screenshare) =>
        users[screenshare.userId]
          ? {
              ...screenshare,
              user: users[screenshare.userId],
            }
          : undefined
      )
      .filter(Boolean) as UserScreenshareSource[];

    return formattedScreenshares.sort((a, b) => b.timestamp - a.timestamp);
  }
);

export const selectLastOpenedScreenshare = (state: RootState) =>
  state.layout.order.screenshareStreams.reduce(
    (latest, current) => (!latest || current.timestamp > latest.timestamp ? current : latest),
    state.layout.order.screenshareStreams[0]
  );

export const selectLastOpenedExternalStream = (state: RootState) =>
  state.layout.order.externalStreams.reduce(
    (latest, current) => (!latest || current.timestamp > latest.timestamp ? current : latest),
    state.layout.order.externalStreams[0]
  );

export const selectExternalStreams = (state: RootState) => state.layout.order.externalStreams;

export const selectHistoricalSpeakers = (state: RootState) => state.layout.order.historicalSpeakers;

export const selectLastHistoricalSpeaker = createSelector(
  [selectHistoricalSpeakers, selectUsersWithoutLocal, selectLocalUser],
  (speakers, users, localUser) => {
    if (speakers.length === 0 && users.length === 0) {
      return localUser;
    }

    if (speakers.length === 0) {
      const broadcastingUser = users.find((user) => user.audioEnabled);

      if (broadcastingUser) {
        return broadcastingUser;
      }
    }

    const activeSpeaker = speakers[0];

    return users.find((user) => user.id === activeSpeaker?.userId) || localUser;
  }
);

export const selectActiveSpeaker = (state: RootState) =>
  state.layout.order.orderedStreams.find(
    (source) => source.userId === state.streaming.activeSpeakerId
  );

export const selectActiveSpeakerId = (state: RootState) => state.streaming.activeSpeakerId;

export default layoutOrderSlice.reducer;
